import React, { FC } from 'react'
import {
  Flex,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Box,
  Text,
} from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { theme } from '@moonpig/launchpad-theme'
import { useFindLocaleText } from '../../../../text-localisation'
import { groupCitiesByLetter } from '../groupCitiesByLetter'
import { BulletedLocationListItem } from './BulletedLocationListItem'

const StyledTabbedListWrapper = styled(Flex)`
  overflow-x: scroll;

  &:after {
    content: '';
    position: sticky;
    margin-top: -50px;
    z-index: 1;
    bottom: 0;
    left: 0;
    pointer-events: none;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0) 90%,
      rgba(255, 255, 255, 0.6) 100%
    );
    width: 100%;
  }
`

const StyledTabbedList = styled(TabList)`
  ${s({
    flexWrap: 'wrap',
  })}
`

const StyledTab = styled(Tab)`
  ${s({
    minWidth: '7.3%',
  })}
`

const StyledText = styled(Text)<{ hasLocations: boolean }>`
  ${({ hasLocations }) =>
    s({
      typography: hasLocations ? 'typeButtonUnderlined' : 'typeBodyLabel',
      color: hasLocations
        ? theme.colors.colorTextHeadline
        : theme.colors.colorBlack20,
    })}
`

const NoLocations: FC = () => {
  const localiseText = useFindLocaleText()
  return (
    <ul>
      <Text>{localiseText('find.no_locations_to_show')}</Text>
    </ul>
  )
}

export const TabbedLocations: FC<{
  postcodesByCity: Record<string, string[]>
}> = ({ postcodesByCity }) => {
  const alphabet = [...Array(26)].map((_, i) => String.fromCharCode(i + 65))

  const citiesByLetter = groupCitiesByLetter(postcodesByCity)

  const locationsAvailable = (key: string) =>
    key in citiesByLetter && Object.keys(citiesByLetter[key]).length > 0

  return (
    <Tabs data-testid="locations-tabs" defaultValue="A">
      <StyledTabbedListWrapper width={1} flexWrap={'wrap'} pb={4} mb={4}>
        <StyledTabbedList>
          {alphabet.map((char: string) => (
            <StyledTab data-testid={char} value={`${char}`} key={char}>
              <StyledText hasLocations={locationsAvailable(char)}>
                {char}
              </StyledText>
            </StyledTab>
          ))}
        </StyledTabbedList>
      </StyledTabbedListWrapper>
      <TabPanels>
        {alphabet.map((char: string) => (
          <TabPanel key={char} value={`${char}`}>
            <Box pb={6}>
              {char in citiesByLetter ? (
                <BulletedLocationListItem
                  postcodesByCity={citiesByLetter[char]}
                />
              ) : (
                <NoLocations />
              )}
            </Box>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  )
}
